import { selectEnv } from "@app/config";
import {
  fetchActivePlans,
  fetchPlans,
  getStackType,
  selectFirstActivePlan,
  selectIsAccountOwner,
  selectIsBillingAdmin,
  selectPlanByActiveId,
  selectStacksByOrgAsList,
} from "@app/deploy";
import {
  selectHasManyOrgs,
  selectOrganizationSelectedId,
} from "@app/organizations";
import { useQuery, useSelector } from "@app/react";
import {
  billingAddressUrl,
  billingContactsUrl,
  billingInvoicesUrl,
  billingPaymentMethodsUrl,
  plansUrl,
  securitySettingsUrl,
  settingsAccountIntegrationsUrl,
  settingsLegalAgreementsUrl,
  settingsProfileUrl,
  settingsTokenUrl,
  sshSettingsUrl,
  teamContactsUrl,
  teamManagedAiUrl,
  teamMembersUrl,
  teamPendingInvitesUrl,
  teamRolesUrl,
  teamScimUrl,
  teamSsoUrl,
} from "@app/routes";
import cn from "classnames";
import { NavLink } from "react-router-dom";
import { useTrialNotice } from "../hooks/use-trial-notice";
import { IconLock } from "./icons";
import { tokens } from "./tokens";
import { Tooltip } from "./tooltip";

const active = "bg-off-white font-semibold text-black focus:text-black";
const inactive = "text-black-500 hover:bg-black-50 hover:text-black";
const navButton =
  "group flex items-center p-2 text-base rounded-md hover:no-underline";

export function SettingsSidebar() {
  const env = useSelector(selectEnv);
  const orgId = useSelector(selectOrganizationSelectedId);
  const hasManyOrgs = useSelector(selectHasManyOrgs);
  const isAccountOwner = useSelector((s) => selectIsAccountOwner(s, { orgId }));
  const isBillingAdmin = useSelector((s) => selectIsBillingAdmin(s, { orgId }));
  const { hasTrialNoPayment } = useTrialNotice();

  useQuery(fetchPlans());
  useQuery(fetchActivePlans({ orgId }));
  const activePlan = useSelector(selectFirstActivePlan);
  const plan = useSelector((s) =>
    selectPlanByActiveId(s, { id: activePlan.planId }),
  );
  const stacks = useSelector(selectStacksByOrgAsList);
  const hasDedicatedStack = stacks.some((s) => getStackType(s) === "dedicated");
  const isBaaEligible =
    hasDedicatedStack ||
    plan.name === "production" ||
    plan.name === "enterprise";

  const navLink = ({ isActive }: { isActive: boolean }) =>
    cn(navButton, { [inactive]: !isActive, [active]: isActive });

  return (
    <nav className="flex flex-col">
      <div>
        <h4 className={`${tokens.type.h4} ml-2`}>Account Settings</h4>

        <NavLink className={navLink} to={settingsProfileUrl()}>
          Profile
        </NavLink>

        <NavLink className={navLink} to={securitySettingsUrl()}>
          Security
          <Tooltip text="Reauthentication Required" fluid>
            <IconLock variant="sm" className="ml-1 opacity-60" />
          </Tooltip>
        </NavLink>

        <NavLink className={navLink} to={sshSettingsUrl()}>
          SSH Keys
          <Tooltip text="Reauthentication Required" fluid>
            <IconLock variant="sm" className="ml-1 opacity-60" />
          </Tooltip>
        </NavLink>

        <NavLink className={navLink} to={settingsTokenUrl()}>
          Access Tokens
        </NavLink>

        <hr className="mt-3 mx-2" />
      </div>

      <div>
        <h4 className={`${tokens.type.h4} ml-2 mt-4`}>Team Settings</h4>

        <NavLink className={navLink} to={teamMembersUrl()}>
          Members
        </NavLink>

        <NavLink className={navLink} to={teamPendingInvitesUrl()}>
          Pending Invites
        </NavLink>

        <NavLink className={navLink} to={teamRolesUrl()}>
          Roles
        </NavLink>

        {isAccountOwner ? (
          <NavLink className={navLink} to={teamSsoUrl()}>
            Single Sign-On
          </NavLink>
        ) : (
          <span className={navLink({ isActive: false })}>
            Single Sign-On
            <Tooltip text="Must be account owner" fluid>
              <IconLock variant="sm" className="ml-1 opacity-60" />
            </Tooltip>
          </span>
        )}

        {isAccountOwner && !hasManyOrgs && !hasTrialNoPayment ? (
          <NavLink className={navLink} to={teamScimUrl()}>
            Provisioning
          </NavLink>
        ) : (
          <span className={navLink({ isActive: false })}>
            Provisioning
            <Tooltip
              text={
                hasTrialNoPayment
                  ? "Feature not available for trial users"
                  : "Must be account owner with a single assigned organization"
              }
              fluid
            >
              <IconLock variant="sm" className="ml-1 opacity-60" />
            </Tooltip>
          </span>
        )}

        {isAccountOwner ? (
          <NavLink className={navLink} to={teamContactsUrl()}>
            Organization Settings
          </NavLink>
        ) : (
          <span className={navLink({ isActive: false })}>
            Organization Settings
            <Tooltip text="Must be account owner" fluid>
              <IconLock variant="sm" className="ml-1 opacity-60" />
            </Tooltip>
          </span>
        )}

        {(env.isDev || env.externalAwsFeatureOrgIds.includes(orgId)) && (
          <NavLink className={navLink} to={settingsAccountIntegrationsUrl()}>
            Account Integrations
          </NavLink>
        )}

        {isAccountOwner ? (
          <NavLink className={navLink} to={teamManagedAiUrl()}>
            Managed AI
          </NavLink>
        ) : null}

        <hr className="mt-3 mx-2" />
      </div>

      <div>
        <h4 className={`${tokens.type.h4} ml-2 mt-4`}>Billing</h4>

        {isAccountOwner ? (
          <NavLink className={navLink} to={plansUrl()}>
            Plans
          </NavLink>
        ) : (
          <span className={navLink({ isActive: false })}>
            Plans
            <Tooltip text="Must be account owner" fluid>
              <IconLock variant="sm" className="ml-1 opacity-60" />
            </Tooltip>
          </span>
        )}

        {isBillingAdmin ? (
          <NavLink className={navLink} to={billingInvoicesUrl()}>
            Invoices
          </NavLink>
        ) : (
          <span className={navLink({ isActive: false })}>
            Invoices
            <Tooltip text="Must be account owner or billing admin owner" fluid>
              <IconLock variant="sm" className="ml-1 opacity-60" />
            </Tooltip>
          </span>
        )}
        {isBillingAdmin ? (
          <>
            <NavLink className={navLink} to={billingPaymentMethodsUrl()}>
              Payment Methods
            </NavLink>
            <NavLink className={navLink} to={billingContactsUrl()}>
              Contacts
            </NavLink>
            <NavLink className={navLink} to={billingAddressUrl()}>
              Billing Address
            </NavLink>
          </>
        ) : null}
      </div>

      <div>
        <hr className="mt-3 mx-2" />
        <h4 className={`${tokens.type.h4} ml-2 mt-4`}>Legal and Compliance</h4>
        {isBaaEligible ? (
          <NavLink className={navLink} to={settingsLegalAgreementsUrl()}>
            Agreements
          </NavLink>
        ) : (
          <span className={navLink({ isActive: false })}>
            Agreements
            <Tooltip text="Production or Enterprise plan required" fluid>
              <IconLock variant="sm" className="ml-1 opacity-60" />
            </Tooltip>
          </span>
        )}
      </div>
    </nav>
  );
}
